import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Bus, Radio, Clock, AlertTriangle, Sparkles, Activity, CheckCircle2,
  PlayCircle, Gauge, TrendingUp, MapPin, ShieldAlert, Zap, Target,
  ArrowRight, RefreshCw, History, Wifi, WifiOff,
} from "lucide-react";
import { PageShell, PageHeader } from "@/components/ds";
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";

/**
 * Resumé Executif — replaces the previous reporting dashboard.
 *
 * Reading order (top → bottom):
 *   1. Exploitation live KPIs   (5 cards)
 *   2. Exploitation jour KPIs   (5 cards)
 *   3. Mini carte réseau         (live vehicles, colored by status)
 *   4. Alertes critiques         (incidents + bus offline + retards critiques)
 *   5. Missions du jour
 *   6. Retards détectés          (ligne / véhicule / retard / chauffeur)
 *   7. Optimisations IA          (top 5 by priority × gain)
 *   8. Activité récente          (mixed feed)
 *
 * The page polls /dashboard/summary every 30s for near-real-time freshness.
 */
export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const loadAll = async () => {
    try {
      const [{ data: s }, vresp] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/logibus/live/vehicles").catch(() => ({ data: { vehicles: [] } })),
      ]);
      setSummary(s);
      setVehicles(vresp.data?.vehicles || []);
      setRefreshedAt(new Date());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading && !summary) {
    return (
      <PageShell testId="admin-dashboard">
        <PageHeader eyebrow="Vue d'ensemble" title="Dashboard" subtitle="Chargement…" />
      </PageShell>
    );
  }
  const s = summary || {};

  return (
    <PageShell testId="admin-dashboard">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
        <PageHeader
          eyebrow="Résumé exécutif · Vue temps réel"
          title="Dashboard"
          subtitle="L'état de votre exploitation, en moins de 10 secondes."
        />
        <button onClick={loadAll} data-testid="dashboard-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-900 bg-[#FFC60A] text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a]">
          <RefreshCw size={12} /> Actualiser
          {refreshedAt && <span className="text-[10px] font-mono text-zinc-700 ml-1">{refreshedAt.toLocaleTimeString("fr-CH").slice(0, 5)}</span>}
        </button>
      </div>

      {/* ─── 1. KPI Exploitation live (5 cards) ─────────────────────────── */}
      <SectionEyebrow label="Exploitation live · Vue d'ensemble" />
      <div className="grid grid-cols-2 lg:grid-cols-5 border border-zinc-900 mb-4" data-testid="kpi-live">
        <BigKpi icon={Bus}            label="Bus en service"  value={s.buses_active ?? "—"}        accent="#10B981" to="/admin/live-tracking" testId="kpi-buses-active" />
        <BigKpi icon={Radio}          label="Affectations"    value={s.active_affectations ?? "—"}                   to="/admin/affectations" testId="kpi-affectations" />
        <BigKpi icon={Clock}          label="Retards"         value={s.buses_delayed ?? 0}         accent={s.buses_delayed > 0 ? "#F59E0B" : null} to="/admin/incidents?tab=delays" testId="kpi-buses-delayed" />
        <BigKpi icon={AlertTriangle}  label="Alertes critiques" value={(s.buses_critical || 0) + (s.incidents_open || 0)} accent={((s.buses_critical || 0) + (s.incidents_open || 0)) > 0 ? "#EF4444" : null} to="/admin/live-tracking" testId="kpi-alerts" />
        <BigKpi icon={Sparkles}       label="Optimisations IA" value={s.ai_recommendations_open ?? 0} accent={s.ai_recommendations_open > 0 ? "#0A0A0B" : null} dark={s.ai_recommendations_open > 0} to="/admin/ai-recommendations" testId="kpi-ai" />
      </div>

      {/* ─── 2. KPI Exploitation jour (5 cards) ─────────────────────────── */}
      <SectionEyebrow label="Exploitation du jour" />
      <div className="grid grid-cols-2 lg:grid-cols-5 border border-zinc-900 mb-6" data-testid="kpi-day">
        <BigKpi icon={Target}       label="Missions"        value={s.missions_today ?? "—"}    to="/admin/affectations" testId="kpi-missions" />
        <BigKpi icon={CheckCircle2} label="Terminées"       value={s.missions_completed ?? 0}  accent="#10B981" testId="kpi-completed" />
        <BigKpi icon={PlayCircle}   label="Actives"         value={s.missions_active ?? 0}     accent="#FFC60A" testId="kpi-active-missions" />
        <BigKpi icon={Clock}        label="Retard moyen"    value={`${s.avg_delay_min ?? 0} min`} testId="kpi-avg-delay" />
        <BigKpi icon={TrendingUp}   label="Ponctualité"     value={`${s.punctuality_pct ?? 100}%`} accent={(s.punctuality_pct ?? 100) >= 90 ? "#10B981" : "#F59E0B"} testId="kpi-punctuality" />
      </div>

      {/* ─── 3. Mini carte réseau + 4. Alertes critiques ─────────────────── */}
      <div className="grid lg:grid-cols-3 gap-3 mb-6">
        <div className="lg:col-span-2 border border-zinc-900 bg-white" data-testid="dashboard-mini-map">
          <div className="px-3 py-2 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Mini carte réseau</div>
              <div className="font-display font-bold text-sm tracking-tight">{vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""} suivi{vehicles.length > 1 ? "s" : ""}</div>
            </div>
            <Link to="/admin/live-tracking" className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
              Suivi Live <ArrowRight size={11} />
            </Link>
          </div>
          <MiniMap vehicles={vehicles} />
        </div>
        <CriticalAlerts alerts={s.critical_alerts || []} />
      </div>

      {/* ─── 6. Retards détectés + 7. Optimisations IA ─────────────────── */}
      <div className="grid lg:grid-cols-2 gap-3 mb-6">
        <DelaysPanel delays={s.recent_delays || []} />
        <AIPanel recos={s.ai_recommendations_top || []} />
      </div>

      {/* ─── 8. Activité récente ──────────────────────────────────────── */}
      <ActivityPanel events={s.recent_activity || []} />
    </PageShell>
  );
}

/* ── helpers ───────────────────────────────────────────────────────────── */

function SectionEyebrow({ label }) {
  return (
    <div className="mb-2">
      <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">{label}</div>
    </div>
  );
}

function BigKpi({ icon: Icon, label, value, accent, dark = false, to, testId }) {
  const inner = (
    <div className={`p-3 lg:p-4 border-r border-zinc-900 last:border-r-0 hover:bg-zinc-50 transition-colors h-full ${dark ? "bg-zinc-900 text-white hover:bg-zinc-800" : ""}`}>
      <div className="flex items-center gap-1.5">
        <Icon size={12} style={!dark && accent ? { color: accent } : undefined} className={dark ? "text-[#FFC60A]" : "text-zinc-700"} />
        <span className={`text-[10px] font-mono tracking-[0.2em] uppercase ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{label}</span>
      </div>
      <div className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tighter leading-none"
        style={!dark && accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
  return to ? <Link to={to} data-testid={testId}>{inner}</Link> : <div data-testid={testId}>{inner}</div>;
}

function vehicleIcon(v) {
  const status = (v.status || "").toUpperCase();
  const offline = !v.lat || !v.lon || status === "OFFLINE" || status === "NO_GPS";
  const delayMin = Number(v.forced_delay_minutes ?? v.delay_minutes ?? 0);
  let color = "#10B981";        // ON_TIME / OK
  if (offline) color = "#71717A";
  else if (delayMin >= 7 || status === "CRITICAL") color = "#EF4444";
  else if (delayMin >= 3 || status === "LIGHT_DELAY" || status === "DELAY_LIGHT" || status === "DELAY_IMPORTANT") color = "#F59E0B";
  return L.divIcon({
    className: "",
    html: `<div style="width:14px;height:14px;background:${color};border:2px solid #0A0A0B;border-radius:999px;box-shadow:0 1px 2px rgba(0,0,0,0.3);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function MiniMap({ vehicles }) {
  const valid = vehicles.filter((v) => typeof v.lat === "number" && typeof v.lon === "number");
  const center = useMemo(() => {
    if (!valid.length) return [46.51, 6.49];
    const lat = valid.reduce((a, p) => a + p.lat, 0) / valid.length;
    const lng = valid.reduce((a, p) => a + p.lon, 0) / valid.length;
    return [lat, lng];
  }, [valid]);

  if (!valid.length) {
    return (
      <div className="h-[260px] flex flex-col items-center justify-center text-xs text-zinc-400 bg-zinc-50">
        <MapPin size={28} className="text-zinc-300 mb-2" />
        Aucun véhicule géolocalisé actuellement
      </div>
    );
  }
  return (
    <div className="h-[260px] relative">
      <MapContainer center={center} zoom={10} scrollWheelZoom={false} attributionControl={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {valid.map((v) => (
          <Marker key={v.vehicle_id || v.navixy_tracker_id} position={[v.lat, v.lon]} icon={vehicleIcon(v)}>
            <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
              <div className="text-[11px]">
                <strong>{v.label || v.bus_name || v.vehicle_id}</strong>
                {v.driver_name && <div>{v.driver_name}</div>}
                {v.route_short_name && <div>Ligne {v.route_short_name}</div>}
                {v.forced_delay_minutes > 0 && <div style={{ color: "#b45309" }}>+{v.forced_delay_minutes} min</div>}
              </div>
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
      <div className="absolute bottom-2 left-2 bg-white/90 border border-zinc-900 px-2 py-1 text-[10px] font-mono flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> OK</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Retard</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Critique</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-500" /> Hors ligne</span>
      </div>
    </div>
  );
}

function CriticalAlerts({ alerts }) {
  return (
    <div className="border border-zinc-900 bg-white flex flex-col" data-testid="critical-alerts">
      <div className="px-3 py-2 border-b border-zinc-900 bg-red-50 flex items-center gap-2">
        <ShieldAlert size={14} className="text-red-700" />
        <div className="flex-1">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-red-800">Alertes critiques</div>
          <div className="font-display font-black text-sm tracking-tight text-red-900">{alerts.length} en cours</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[260px]">
        {alerts.length === 0 ? (
          <div className="p-6 text-center text-xs text-emerald-700">
            <CheckCircle2 size={28} className="mx-auto text-emerald-400 mb-2" />
            Aucune alerte critique en cours.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {alerts.map((a, i) => (
              <Link key={i} to={a.link || "/admin/live-tracking"} data-testid={`alert-${i}`}
                className="block px-3 py-2 hover:bg-red-50">
                <div className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${a.severity === "critical" ? "bg-red-600" : "bg-amber-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{a.label}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">{a.detail}</div>
                  </div>
                  <ArrowRight size={10} className="text-zinc-400 mt-1.5" />
                </div>
              </Link>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DelaysPanel({ delays }) {
  return (
    <div className="border border-zinc-900 bg-white" data-testid="delays-panel">
      <div className="px-3 py-2 border-b border-zinc-900 bg-amber-50 flex items-center gap-2">
        <Clock size={14} className="text-amber-700" />
        <div className="flex-1">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-amber-800">Retards détectés</div>
          <div className="font-display font-black text-sm tracking-tight">{delays.length} signal{delays.length > 1 ? "s" : ""}</div>
        </div>
        <Link to="/admin/incidents?tab=delays" className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900">Tout voir →</Link>
      </div>
      <div>
        {delays.length === 0 ? (
          <div className="p-6 text-center text-xs text-emerald-700">
            <CheckCircle2 size={24} className="mx-auto text-emerald-400 mb-1" /> Aucun retard récent.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {delays.map((d, i) => {
              const min = Number(d.delay_minutes ?? 0);
              const tone = min >= 7 ? "text-red-700" : min >= 3 ? "text-amber-700" : "text-zinc-600";
              return (
                <li key={i} data-testid={`delay-${i}`} className="px-3 py-2 flex items-center gap-2 hover:bg-zinc-50">
                  <span className="font-display font-black text-base tracking-tighter w-12 flex-shrink-0">{d.route_short_name || "—"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{d.vehicle_label || d.stop_name || "—"}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">
                      {d.driver_name ? `${d.driver_name} · ` : ""}{d.stop_name || "—"}
                    </div>
                  </div>
                  <span className={`font-mono text-xs font-bold ${tone}`}>+{min} min</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function AIPanel({ recos }) {
  return (
    <div className="border border-zinc-900 bg-zinc-900 text-white" data-testid="ai-panel">
      <div className="px-3 py-2 border-b border-zinc-700 flex items-center gap-2">
        <Sparkles size={14} className="text-[#FFC60A]" />
        <div className="flex-1">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400">Optimisations IA</div>
          <div className="font-display font-black text-sm tracking-tight">{recos.length} recommandation{recos.length > 1 ? "s" : ""}</div>
        </div>
        <Link to="/admin/ai-recommendations" className="text-[10px] font-mono uppercase tracking-widest text-[#FFC60A] hover:underline">Tout voir →</Link>
      </div>
      <div>
        {recos.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-400">
            <Zap size={24} className="mx-auto text-zinc-600 mb-1" /> Aucune optimisation en attente.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-700">
            {recos.map((r, i) => {
              const prio = (r.priority || "").toLowerCase();
              const prioColor = prio === "high" || prio === "haute" ? "#EF4444" : prio === "medium" || prio === "moyenne" ? "#F59E0B" : "#10B981";
              return (
                <Link to="/admin/ai-recommendations" key={r.id || i} data-testid={`ai-reco-${i}`}
                  className="block px-3 py-2 hover:bg-zinc-800">
                  <div className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: prioColor }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white line-clamp-2">{r.title || "Recommandation"}</div>
                      <div className="text-[10px] font-mono text-zinc-400 line-clamp-1 mt-0.5">{r.summary}</div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex gap-2">
                        {r.priority && <span className="uppercase">{r.priority}</span>}
                        {r.impact && <span className="text-emerald-400">· {r.impact}</span>}
                      </div>
                    </div>
                    <ArrowRight size={10} className="text-zinc-500 mt-1.5 flex-shrink-0" />
                  </div>
                </Link>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ActivityPanel({ events }) {
  return (
    <div className="border border-zinc-900 bg-white" data-testid="activity-panel">
      <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-50 flex items-center gap-2">
        <History size={14} />
        <div className="flex-1">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Activité récente</div>
          <div className="font-display font-black text-sm tracking-tight">{events.length} événement{events.length > 1 ? "s" : ""}</div>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="p-6 text-center text-xs text-zinc-400">Aucun événement récent.</div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {events.map((e, i) => (
            <li key={i} data-testid={`activity-${i}`} className="px-3 py-2 flex items-center gap-3">
              <span className="font-mono text-[10px] text-zinc-500 w-14 flex-shrink-0">{formatTime(e.ts)}</span>
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  e.kind === "affectation" ? "bg-blue-500" :
                  e.kind === "mission_done" ? "bg-emerald-500" :
                  e.kind === "incident_closed" ? "bg-purple-500" :
                  "bg-zinc-400"
                }`}
              />
              <span className="text-xs text-zinc-700 flex-1 min-w-0 truncate">{e.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}
